// @flow

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withJob } from 'react-jobs';
import styled from 'styled-components';
import Link from 'react-router-dom/Link';
import Helmet from 'react-helmet';

import type { Product } from '../../types/model';

import {
  selectors as FromProducts,
  requestProduct,
} from '../../redux/modules/products';
import {
  selectors as FromCategories,
} from '../../redux/modules/categories';

import { Loading } from '../StyledComponents';

import PictureSlider from './PictureSlider';
import ProductContent from './ProductContent';

const Root = styled.div`
  position: relative;

  display: flex;
  min-height: 420px;
  max-height: 510px;

  background-color: #3C3C3C;
`;

const Header = styled.header`
  margin-bottom: 15px;
`;

const ContentWrap = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  margin: 15px 90px;

  overflow: hidden;

  color: #FCFCFC;
`;

const ProductTitle = styled.h1`
  margin: 0;

  text-transform: uppercase;

  font-weight: normal;
  line-height: 1.2;

  color: ${props => props.color ? props.color : '#FCFCFC'};
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
`;

const Sidebar = styled.div`
  width: 300px;
  margin-right: 30px;
`;

const SideSection = styled.div`
  margin-bottom: 15px;
`;

const SideSectionDescription = styled.div`
  font-size: 15px;
  font-family: 'Georgia', serif;
`;

const Content = styled.div`
  flex: 1;
`;

const PicturesWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 360px;
`

const CloseButton = styled(Link)`
  position: absolute;
  left: 30px;
  top: 15px;
  transform: translateX(-50%);

  border: none;

  text-decoration: none;

  font-size: 36px;
  font-weight: 300;

  background: transparent;
  color: ${props => props.color ? props.color : '#FCFCFC'};

  cursor: pointer;
  outline: none;
`

const SinglePictureWrap = styled.div`
  width: 270px;
  height: 270px;

  border-radius: 50%;
  overflow: hidden;

  background-color: #fff;

  user-select: none;
  user-drag: none;
  pointer-events: none;
`;

const SinglePicture = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const StyledLoading = styled(Loading)`
  background-color: #fff;
`;

type Props = {
  color: ?string,
  product: Product,
}

function ProductDrawer({ color, product }: Props) {
  return (
    <Root>
      <Helmet>
        <title>{product.title}</title>
      </Helmet>

      <ContentWrap>

        <Header>
          <ProductTitle color={color}>
            {product.title}
          </ProductTitle>
        </Header>

        <ProductInfo>
          <Sidebar>

            <SideSection>
              <SideSectionDescription
                dangerouslySetInnerHTML={{ __html: product.body }}
              />
            </SideSection>

          </Sidebar>
          <Content>
            <ProductContent
              overview={product.overview}
              indications={product.indications}
              techSpecs={product.techSpecs}
              color={color}
            />
          </Content>
          <PicturesWrap>
            {product.images.length > 1 ? (
              <PictureSlider
                pictures={product.images.map(item => item.image.medium)}
              />
            ) : (
              <SinglePictureWrap>
                {product.images.length > 0 ? <SinglePicture src={product.images[0].image.medium.url} alt={product.title} /> : null}
              </SinglePictureWrap>
            )}
          </PicturesWrap>
        </ProductInfo>

      </ContentWrap>

      <CloseButton
        color={color}
        to={`/doctor/${product.categoryId}`}
      >
        ✕
      </CloseButton>
    </Root>
  );
}

function mapStateToProps(state, props) {
  const productId = parseInt(props.match.params.id, 10);
  const product = FromProducts.getById(state.products, productId);
  const category = product
    ? FromCategories.getById(state.categories, product.categoryId)
    : null;
  const parentCategory = product && category && category.parentId
    ? FromCategories.getById(state.categories, category.parentId)
    : null;

  return ({
    productId,
    product,
    color: category
      ? (category.color ? category.color : (parentCategory && parentCategory.color ? parentCategory.color : '#fff'))
      : '#fff',
  });
}

const mapActionsToProps = {
  requestProduct,
}

export default compose(
  connect(mapStateToProps, mapActionsToProps),
  withJob({
    work: props => props.requestProduct(props.productId),
    LoadingComponent: () => <Root><StyledLoading /></Root>,
  })
)(ProductDrawer);



// WEBPACK FOOTER //
// ./src/components/ProductDrawer/ProductDrawer.js